import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { Sheet } from '@zendeskgarden/react-chrome';
import { Grid, Row, Col } from '@zendeskgarden/react-grid';
import { Button } from '@zendeskgarden/react-buttons';
import { Field, Input, Message } from '@zendeskgarden/react-forms';
import { getColor, mediaQuery } from '@zendeskgarden/react-theming';
import ButtonLink from '../../../components/shortlistLink/button-link';
import { Tab, TabList, Tabs, TabPanel } from '@zendeskgarden/react-tabs';
import { ReactComponent as CopyButton  } from '@zendeskgarden/svg-icons/src/16/copy-stroke.svg';
import { ReactComponent as Clipboard  } from '@zendeskgarden/svg-icons/src/16/check-sm-fill.svg';
import ShortlistService from '../../../services/shortlist.service';
import style from "./index.module.scss";
import { map } from 'lodash';
import { AlertService } from '../../../services/alert.service';

const StyledField = styled(Field)`
  margin: ${props => props.theme.space.md || '16px'} 0;
`;
const StyledRow = styled(Row)<{ isOpen: boolean }>`
	height: ${({ isOpen }) => (isOpen ? 'auto' : '0px')};
  border: none;
  box-shadow: ${({ isOpen }) => (isOpen ? '0 4px 4px 4px rgba(0, 0, 0, 0.1)' : 'none')};
  border-radius: 8px;
  position: absolute;
  z-index: 99999;
`;

const StyledSheet = styled(Sheet)<{ isOpen: boolean }>`
  width: fit-content;
  position: ${({ isOpen }) => (isOpen ? 'static' : 'absolute')};
  overflow: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  ${props => mediaQuery('down', 'sm', props.theme) || ''} {
    width: 100%;

    /* sheet inner wrapper */
    & > div {
      min-width: 100%;
    }
  }
`;

const StyledTabPanel = styled(TabPanel)`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: space-around;
  align-items: center;
  width: 85%;
  margin: 0 auto;
  background-color: #f8f9f9;
  border: 1px solid #d8dcde;
  border-radius: 8px;
  padding: 10px;
  margin-top: 16px;
  color: #68737d;
  font-weight: 500;
  ${props => mediaQuery('down', 'sm', props.theme) || ''} {
    width: 94vw;
    padding: 12px;
    flex-direction: column;
    gap: 8px;
    & Button {
      width: 90%;
    }

    /* sheet inner wrapper */
    & > div {
      min-width: 100%;
    }
  }
`;

const CopyButtonContainer = styled.button`
  width: fit-content;
  padding: 8px;
  background-color: #f8f9f9;
  border: 1px solid #d8dcde;
  border-radius: 4px 8px 8px 4px;
  transition: all 0.3s ease-in-out;
  :hover {
    border: 1px solid #DE701D;
    background-color: #fcede1ff;
  }
  :disabled {
    cursor: not-allowed;
    :hover {
      border: 1px solid #d8dcde;
      background-color: #f8f9f9;
    }
  }
`;

const InputButton = styled(Input)`
  width: 89%;
  border-radius: 8px 4px 4px 8px;
`;

const Small = styled.small<{ date?: boolean }>`
  color: ${props => (props.date ? '#D32F2F' : '#68737D')};
  border: solid 1px #d8dcde;
  background-color: #ffffff;
  border-radius: 4px;
  text-align: center;
  flex-grow: 1;
  font-weight: 500;
  padding: 4px 0;
  @media (max-width: 768px) {
    width: 90%;
  }
`;

const tabs = [
  { id: 'tab-1', label: 'Gerar' },
  { id: 'tab-2', label: 'Renovar' },
  { id: 'tab-3', label: 'Regenerar' },
  { id: 'tab-4', label: 'Bloquear' }
];

interface SheetShortlistState {
	isSheetOpen: boolean;
  selectedTab?: string;
  select: any;
  shortlistLinkToken: string;
  isClipboardCopied?: boolean;
  hasTokenBeenGenerated: boolean;
  tokenRevoked: boolean;
  tabDisabled: boolean;
  buttonDisabled: boolean;
  tokenInfo: any;
}

class SheetShortlist extends React.Component<{opportunityId: string, theme?: any}, SheetShortlistState> {
	constructor(props: any) {
		super(props);
		this.handleOpenSheet = this.handleOpenSheet.bind(this);
    this.generateToken = this.generateToken.bind(this);
    this.renew = this.renew.bind(this);
    this.handleBlockToken = this.handleBlockToken.bind(this);
    this.regenerateToken = this.regenerateToken.bind(this);
	}
	sheetId: string = 'sheet-token';
  
	state: SheetShortlistState = {
    isSheetOpen: false,
    selectedTab: 'tab-1',
    select: null,
    shortlistLinkToken: '',
    isClipboardCopied: false,
    hasTokenBeenGenerated: false,
    tokenRevoked: false,
    tabDisabled: false,
    buttonDisabled: false,
    tokenInfo: null
  }

  async componentDidMount(): Promise<void> {

    try {
      const tokenInfo = await ShortlistService.getShortlistLinkByOpportunity(this.props.opportunityId);
      console.log('Fetched shortlist link token:', tokenInfo);
      if (!tokenInfo.data) {
        return;
      }
      this.setState({
        shortlistLinkToken: `${globalThis.location.origin}/shortlist/${tokenInfo.data.token}`,
        hasTokenBeenGenerated: true,
        tokenInfo: tokenInfo.data,
        tabDisabled: tokenInfo.data.status === 'REVOKED',
        buttonDisabled: tokenInfo.data.status === 'BLOCKED',
      });
    } catch (error) {
      console.error('Error fetching shortlist link token:', error);
    }
  }

  getTabStyle = (tabId: string) => {
    if (tabId === 'tab-1') return {border: 'none', borderTopLeftRadius: '6px'};
    if (tabId === 'tab-4') return {border: 'none', borderTopRightRadius: '6px'};
    return {border: 'none'};
  }

	handleOpenSheet = () => {
		this.setState((prevState: SheetShortlistState) => ({ isSheetOpen: !prevState.isSheetOpen }));
	}

  generateToken = async () => {
    this.setState({ hasTokenBeenGenerated: true });
    try {
      const response = await ShortlistService.generateShortlistLink(this.props.opportunityId);

      this.setState({
        shortlistLinkToken: `${globalThis.location.origin}/shortlist/${response.data.token}`,
      });
      AlertService.success('Link gerado com sucesso!');
    } catch (error) {
      console.error('Error generating shortlist link token:', error);
      AlertService.error('Erro ao gerar o link de shortlist.');
      this.setState({ hasTokenBeenGenerated: false });
    }
  }

  renew = async () => {
    try {
      const response = await ShortlistService.renewShortlistLink(this.state.tokenInfo._id);
      
      this.setState({
        tokenInfo: response.data,
      });

      AlertService.success('Link renovado com sucesso!');
    } catch (error) {
      AlertService.error('Erro ao renovar o link de shortlist.');
      console.error('Error renewing shortlist link token:', error);
    }
  }

  handleBlockToken = async () => {
    const isblockded = this.state.tokenInfo.status === 'BLOCKED';
    try {
      const response = await ShortlistService.blockShortlistLink(this.state.tokenInfo._id, { blocked: isblockded });
      this.setState({ buttonDisabled: response.data.status === 'BLOCKED', tokenInfo: response.data });
      AlertService.success(`Link ${isblockded ? 'desbloqueado' : 'bloqueado'} com sucesso!`);
    } catch (error) {
      AlertService.error('Erro ao bloquear o link de shortlist.');
      console.error('Error blocking shortlist link token:', error);
    }
  }

  regenerateToken = async () => {
    try {
      const response = await ShortlistService.regenerateShortlistLink(this.props.opportunityId);

      this.setState({
        shortlistLinkToken: `${globalThis.location.origin}/shortlist/${response.data.token}`,
        hasTokenBeenGenerated: true,
      });
      AlertService.success('Link regenerado com sucesso!');
    } catch (error) {
      AlertService.error('Erro ao regenerar o link de shortlist.');
      console.error('Error regenerating shortlist link token:', error);
    }
  }

  getExpireDate = (tokenInfo: any): ReactElement => {
    if (tokenInfo) {
      return (
        <Small date={new Date(tokenInfo.expireAt).getTime() < Date.now()}>
          {
            (new Date(tokenInfo.expireAt).getTime() < Date.now())
              ? `Token expirado (${new Date(tokenInfo.expireAt).toLocaleDateString()})`
              : `Token expira em: ${new Date(tokenInfo.expireAt).toLocaleDateString()}`
          }
        </Small>
      )
    }
    return (
      <Small className='pr-4 pl-4'>
        Nenhum token gerado. 
      </Small>
    );
  }

	render(): React.ReactNode {
		const { isSheetOpen } = this.state;
		return (
      <div style={{ position: "relative", width: "100%" }}>
        <Grid gutters={false}>
          <Row>
            <Col>
              <StyledField>
                <ButtonLink
                  isOpen={this.handleOpenSheet}
                >
                  Gerar Link
                </ButtonLink>
              </StyledField>
            </Col>
          </Row>
          <StyledRow isOpen={isSheetOpen} justifyContent="end">
            <StyledSheet
              id={this.sheetId}
              isOpen={isSheetOpen}
              focusOnMount
              restoreFocus
              aria-expanded={isSheetOpen}
            >
              <Sheet.Header style={{ border: 'none', padding: '0' }}>
                <Tabs selectedItem={this.state.selectedTab} onChange={selectedTab => this.setState({ selectedTab }) }>
                  <TabList className={style["tabs"]} aria-label="Shortlist Link Actions">
                    {map(tabs, tab => (
                      <Tab
                        style={this.getTabStyle(tab.id)}
                        key={tab.id}
                        disabled={this.state.tabDisabled}
                        className={
                          this.state.selectedTab === tab.id
                            ? [style["tab-style--active"], style["border-transparent"]].join(' ')
                            : style["tab-style"]
                        }
                        item={tab.id}
                      >
                        {tab.label}
                      </Tab>
                    ))}
                  </TabList>
                  <StyledTabPanel item="tab-1">
                    Gerar link de shortlist.
                    <Button
                      onClick={this.generateToken}
                      disabled={this.state.hasTokenBeenGenerated}  
                    >
                      Gerar
                    </Button>
                    {this.getExpireDate(this.state.tokenInfo)}
                  </StyledTabPanel>
                  <StyledTabPanel item="tab-2">
                    Renovar link de shortlist.
                    <Button
                      onClick={this.renew}
                      disabled={this.state.buttonDisabled}
                    >
                      Renovar
                    </Button>
                    {this.getExpireDate(this.state.tokenInfo)}
                  </StyledTabPanel>
                  <StyledTabPanel item="tab-3">
                    Regenerar link de shortlist.
                    <Button
                      onClick={this.regenerateToken}
                      disabled={this.state.buttonDisabled}
                    >
                      Regenerar
                    </Button>
                    {this.getExpireDate(this.state.tokenInfo)}
                  </StyledTabPanel>
                  <StyledTabPanel item="tab-4">
                    Bloquear link de shortlist.
                    <Button
                      onClick={this.handleBlockToken}
                    >
                      {this.state.buttonDisabled ? 'Desbloquear' : 'Bloquear'}
                    </Button>
                    {this.getExpireDate(this.state.tokenInfo)}
                  </StyledTabPanel>
                </Tabs>
              </Sheet.Header>

              <Sheet.Body className={style["sheet-body"]}>
                <InputButton placeholder={this.state.shortlistLinkToken} readOnly />
                <CopyButtonContainer disabled={this.state.tabDisabled || this.state.buttonDisabled}>
                  {
                    this.state.isClipboardCopied
                      ? <Clipboard
                          color={getColor('successHue', 600)}
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.shortlistLinkToken)
                          }}
                        />
                      : <CopyButton
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.shortlistLinkToken);
                            this.setState({ isClipboardCopied: true }, () => {
                              setTimeout(() => {
                                this.setState({ isClipboardCopied: false });
                              }, 2000);
                            });
                          }}
                          color='#DE701D'
                        />
                  }
                </CopyButtonContainer>
                {this.state.isClipboardCopied && (
                  <Message style={{ color: getColor('successHue', 600), marginTop: '8px'}}>
                    Link copiado!
                  </Message>
                )}
              </Sheet.Body>

              <Sheet.Footer style={{ border: 'none', justifyContent: 'center' }}>
                {this.state.tabDisabled && (
                  <Message style={{
                    color: getColor('dangerHue', 600),
                    marginTop: '8px',
                    fontWeight: '500',
                    backgroundColor: '#ffecec',
                    padding: '16px',
                    borderRadius: '4px'
                  }}>
                    O link de shortlist foi revogado e não está mais ativo.
                  </Message>
                )}
              </Sheet.Footer>
            </StyledSheet>
          </StyledRow>
        </Grid>
      </div>
    );
	}
};

export default SheetShortlist;
